
import React from 'react';
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useTranslation } from 'react-i18next';

interface DateSelectorProps {
  startDate: Date | null;
  dueDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onDueDateChange: (date: Date | null) => void;
  error?: string;
}

export const DateSelector = ({
  startDate,
  dueDate,
  onStartDateChange,
  onDueDateChange,
  error
}: DateSelectorProps) => {
  const { t } = useTranslation();

  return (
    <div className="grid gap-4">
      <div className="grid gap-2">
        <Label>{t('common.startDate')}</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !startDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate ? format(startDate, "PPP") : <span>{t('common.pickDate')}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="p-0 bg-background border shadow-lg rounded-md"
            style={{ 
              position: 'fixed',
              zIndex: 99999,
              pointerEvents: 'auto'
            }}
          >
            <div 
              className="relative"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              style={{ pointerEvents: 'auto' }}
            >
              <Calendar
                mode="single"
                selected={startDate || undefined}
                onSelect={onStartDateChange}
                disabled={(date) => dueDate ? date > dueDate : false}
                initialFocus
                className="rounded-md border bg-background"
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid gap-2">
        <Label>{t('common.dueDate')}</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !dueDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dueDate ? format(dueDate, "PPP") : <span>{t('common.pickDate')}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="p-0 bg-background border shadow-lg rounded-md"
            style={{ 
              position: 'fixed',
              zIndex: 99999,
              pointerEvents: 'auto'
            }}
          >
            <div 
              className="relative"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              style={{ pointerEvents: 'auto' }}
            >
              <Calendar
                mode="single"
                selected={dueDate || undefined}
                onSelect={onDueDateChange}
                disabled={(date) => startDate ? date < startDate : false}
                initialFocus
                className="rounded-md border bg-background"
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};
